import React, { forwardRef } from 'react';
import {
  Card,
  GDSCardElevatedProps,
  GDSCardClickAreaProps,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { MasonryDismissAction } from '../../../types/masonry-driver-node-actions';
import { DismissButton } from '../dismiss-button/dismiss-button';

type DismissActionProps = {
  onDismiss?: MasonryDismissAction;
  toolbarButton?: boolean;
  hasFloatingDismissButton?: boolean;
};

export interface ElevatedCardWithClickAreaProps
  extends GDSCardClickAreaProps,
    DismissActionProps {}

export interface ElevatedCardProps
  extends GDSCardElevatedProps,
    DismissActionProps {}

export interface ConditionalLinkCardRendererProps
  extends ElevatedCardWithClickAreaProps,
    DismissActionProps {
  hasLink?: boolean;
}

const ElevatedCardWithClickArea = forwardRef<
  HTMLDivElement,
  ElevatedCardWithClickAreaProps
>(
  (
    {
      css,
      children,
      onCardMouseUp,
      onCardMouseDown,
      imagePosition,
      onDismiss,
      toolbarButton,
      hasFloatingDismissButton,
      className,
    }: ElevatedCardWithClickAreaProps,
    ref: React.Ref<HTMLDivElement>,
  ) => (
    <Card.Elevated
      className={className}
      backgroundColor="secondary"
      css={{
        '&:has(.GDS-card-click-area)': {
          border: '$borderWidths$card solid $onSurfaceBorderCard',
          boxShadow: '$card',
          borderRadius: '$card',
        },
        '&:hover:not(:has(.dismiss-button:hover))': {
          border: '$borderWidths$card solid $colors$interactiveFocusOuter',
        },
        ...css,
      }}
    >
      {onDismiss &&
        (hasFloatingDismissButton ? (
          <DismissButton
            onDismiss={onDismiss}
            toolbarButton={toolbarButton}
            css={{
              position: 'absolute',
              insetBlockStart: '$half',
              insetInlineEnd: '$half',
              zIndex: 2,
            }}
          />
        ) : (
          <StackLayout
            horizontalAlignment="end"
            css={{ padding: '$half $oneAndHalf 0 0' }}
          >
            <DismissButton
              onDismiss={onDismiss}
              toolbarButton={toolbarButton}
            />
          </StackLayout>
        ))}
      <Card.ClickArea
        ref={ref}
        onCardMouseUp={onCardMouseUp}
        onCardMouseDown={onCardMouseDown}
        imagePosition={imagePosition}
        data-testid="clickable-card"
        css={{
          border: 'none',
          '&:hover': {
            border: 'none',
          },
        }}
      >
        {children}
      </Card.ClickArea>
    </Card.Elevated>
  ),
);

const ElevatedCard = forwardRef<HTMLDivElement, ElevatedCardProps>(
  (
    {
      css,
      imagePosition,
      onDismiss,
      toolbarButton,
      children,
      hasFloatingDismissButton = false,
      className,
    }: ElevatedCardProps,
    ref: React.Ref<HTMLDivElement>,
  ) => (
    <Card.Elevated
      ref={ref}
      imagePosition={imagePosition}
      backgroundColor="secondary"
      css={css}
      className={className}
    >
      {onDismiss &&
        (hasFloatingDismissButton ? (
          <DismissButton
            onDismiss={onDismiss}
            toolbarButton={toolbarButton}
            css={{
              position: 'absolute',
              insetBlockStart: '$half',
              insetInlineEnd: '$half',
              zIndex: 2,
            }}
          />
        ) : (
          <StackLayout
            horizontalAlignment="end"
            css={{ padding: '$half $oneAndHalf 0 0' }}
          >
            <DismissButton
              onDismiss={onDismiss}
              toolbarButton={toolbarButton}
            />
          </StackLayout>
        ))}
      {children}
    </Card.Elevated>
  ),
);

export const ConditionalLinkCardRenderer = forwardRef<
  HTMLDivElement,
  ConditionalLinkCardRendererProps
>(
  (
    {
      css,
      children,
      hasLink,
      onCardMouseUp,
      onCardMouseDown,
      imagePosition,
      onDismiss,
      toolbarButton,
      hasFloatingDismissButton,
      className,
    }: ConditionalLinkCardRendererProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const ConditionalLinkCard = hasLink
      ? ElevatedCardWithClickArea
      : ElevatedCard;

    return (
      <ConditionalLinkCard
        ref={ref}
        onCardMouseUp={onCardMouseUp}
        onCardMouseDown={onCardMouseDown}
        css={css}
        imagePosition={imagePosition}
        onDismiss={onDismiss}
        toolbarButton={toolbarButton}
        hasFloatingDismissButton={hasFloatingDismissButton}
        className={className}
      >
        {children}
      </ConditionalLinkCard>
    );
  },
);
